import { InvalidFormField, type ShopCapability } from "@hobbyshopify/backend-client";
import { BaseController, Rx, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { requestCapability } from "../../core/ops/capability-ops";
import { capabilityAccess, contactEmailProblem } from "../../core/primitives/capabilities";
import { errorText } from "../../core/primitives/errors";
import { CapabilityRequestActions, CapabilityRequestState } from "./capability-request-model";

export type CapabilityRequestDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "capabilitiesRepository"
>;

/** Where the shop stands with one capability, and the request for it. The shell loads the capabilities. */
export class CapabilityRequestController extends BaseController implements CapabilityRequestActions {
  readonly state: CapabilityRequestState;
  readonly #onFile = new Rx<string | null>(null);
  readonly #serverEmailError = new Rx<string | null>(null);
  #prefilledFor: string | null = null;
  #sentTo: string | null = null;

  constructor(
    subtree: SubtreeModel,
    readonly deps: CapabilityRequestDeps,
    readonly capability: ShopCapability,
  ) {
    super();
    this.state = new CapabilityRequestState(capability);
    subtree.put(CapabilityRequestState, this.state);
    subtree.put(CapabilityRequestActions, this);
    this.sync(() => {
      const store = deps.storeRepository.selectedStore;
      const capabilities = store ? deps.capabilitiesRepository.for(store.id) : null;
      const access = capabilityAccess(capabilities, capability, store?.myRole ?? null);
      this.state.access.value = access;
      this.state.declined.value =
        access === "requestable" || access === "askOwner"
          ? (capabilities?.declined.find((d) => d.capability === capability) ?? null)
          : null;
      this.#onFile.value = capabilities?.contactEmail ?? null;
      if (store && capabilities && this.#prefilledFor !== store.id) {
        this.#prefilledFor = store.id;
        this.#sentTo = null;
        this.#serverEmailError.value = null;
        this.state.contactEmail.value = capabilities.contactEmail ?? "";
      }
      this.state.replyTo.value = this.#sentTo ?? this.#onFile.value;
    }, [deps.storeRepository, deps.capabilitiesRepository]);
    this.sync(() => {
      this.state.contactEmailError.value =
        this.#serverEmailError.value ?? contactEmailProblem(this.state.contactEmail.value, this.#onFile.value);
    }, [this.state.contactEmail, this.#onFile, this.#serverEmailError]);
  }

  setContactEmail = (value: string) => {
    this.state.contactEmail.value = value;
    this.#serverEmailError.value = null;
  };

  request = async () => {
    const storeId = this.deps.storeRepository.shopId;
    if (!storeId || this.state.access.value !== "requestable" || this.state.contactEmailError.value) return;
    this.state.error.value = null;
    const contactEmail = this.state.contactEmail.value.trim() || undefined;
    const r = await requestCapability({ ...this.deps, storeId, capability: this.capability, contactEmail });
    if (this.isDisposed) return;
    if (r.ok) {
      this.#sentTo = contactEmail ?? r.value.contactEmail;
      this.state.replyTo.value = this.#sentTo;
      return;
    }
    const field = r.errors.find((e) => e instanceof InvalidFormField);
    if (field) this.#serverEmailError.value = field.message;
    else this.state.error.value = errorText(r.errors);
  };
}
